import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import type { EchoPlugin } from '@cash-web/shared-echo-util-main'

import { EchoClient } from './EchoClient'
import { getEchoClientInstance } from './EchoClientSingleton'

export interface EchoPluginContextValue {
  client: EchoClient | null
  registerPlugin: (plugin: EchoPlugin) => void
  unregisterPlugin: (pluginId: string) => void
  getPlugin: <T extends EchoPlugin>(pluginId: string) => T | null
  hasPlugin: (pluginId: string) => boolean
}

const EchoPluginContext = createContext<EchoPluginContextValue | null>(null)

/**
 * Provider that manages Echo client and plugins.
 * Uses the singleton instance for consistency across the app.
 * Plugins can be dynamically registered and unregistered from anywhere in the app.
 */
export const EchoPluginProvider = ({ children }: PropsWithChildren) => {
  const clientRef = useRef<EchoClient | null>(null)
  const pluginsRef = useRef<Map<string, EchoPlugin>>(new Map())
  const [, forceUpdate] = useState({})

  if (!clientRef.current) {
    clientRef.current = getEchoClientInstance()
  }

  const registerPlugin = useCallback((plugin: EchoPlugin) => {
    const client = clientRef.current
    if (!client) return

    if (pluginsRef.current.has(plugin.id)) {
      const existingPlugin = pluginsRef.current.get(plugin.id)
      if (existingPlugin) {
        client.removePlugin(existingPlugin)
      }
    }

    pluginsRef.current.set(plugin.id, plugin)
    client.addPlugin(plugin)
    forceUpdate({})
  }, [])

  const unregisterPlugin = useCallback((pluginId: string) => {
    const client = clientRef.current
    if (!client) return

    const plugin = pluginsRef.current.get(pluginId)
    if (plugin) {
      client.removePlugin(plugin)
      pluginsRef.current.delete(pluginId)
      forceUpdate({})
    }
  }, [])

  const getPlugin = useCallback(<T extends EchoPlugin>(pluginId: string): T | null => {
    return (pluginsRef.current.get(pluginId) as T) || null
  }, [])

  const hasPlugin = useCallback((pluginId: string): boolean => {
    return pluginsRef.current.has(pluginId)
  }, [])

  const contextValue: EchoPluginContextValue = useMemo(
    () => ({
      client: clientRef.current,
      registerPlugin,
      unregisterPlugin,
      getPlugin,
      hasPlugin,
    }),
    [registerPlugin, unregisterPlugin, getPlugin, hasPlugin]
  )

  return <EchoPluginContext.Provider value={contextValue}>{children}</EchoPluginContext.Provider>
}

export const useEchoPlugins = () => {
  const context = useContext(EchoPluginContext)
  if (!context) {
    throw new Error('useEchoPlugins must be used within EchoPluginProvider')
  }
  return context
}

export const useEchoPluginsSafe = () => {
  return useContext(EchoPluginContext)
}

export const useEchoPlugin = (createPlugin: (client: EchoClient) => EchoPlugin) => {
  const { client, registerPlugin, unregisterPlugin } = useEchoPlugins()
  const pluginRef = useRef<EchoPlugin | null>(null)

  useEffect(() => {
    if (!client) return

    const plugin = createPlugin(client)
    pluginRef.current = plugin
    registerPlugin(plugin)

    return () => {
      if (pluginRef.current) {
        unregisterPlugin(pluginRef.current.id)
      }
    }
  }, [client, registerPlugin, unregisterPlugin, createPlugin])

  return pluginRef.current
}
